import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "option"];
import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import I18n from 'I18n';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import UISelect from 'UIComponents/input/UISelect';
import ForecastTypeDescription from 'ui-deal-management-components-lib/components/forecastTypeDescription/ForecastTypeDescription';
import { getForecastTypes, getIsDealStageView, getSelectedForecastTypeIsDefault } from '../../selectors/forecastSelectors';
import { getSelectedForecastType } from '../../selectors/forecastSelectors';
import { getHasSuperAdminScope } from '../../selectors/authSelectors';
import { getProperties, getPropertiesStatus } from '../../selectors/properties';
import { FILTERS } from '../../constants/FiltersConstants';
import { getForecastTypeDescription } from '../../utils/forecastTypes';
import { CreateForecastButton } from '../fmo/CreateForecastButton';
import ForecastSelectorTourStep from '../tour/fmo/ForecastSelectorTourStep';
import { STARTED, UNINITIALIZED } from '../../constants/RequestStatusTypes';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { trackSwitchForecastType } from '../../utils/usageTracker';
import { REP_DETAIL, REP_OVERVIEW } from '../../constants/Events';
import { View } from 'foundations-components/components/View';
import { setFilter } from '../../actions/FiltersActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const forecastTypeSort = (a, b) => {
  if (a.defaultType) {
    return -1;
  }
  if (b.defaultType) {
    return 1;
  }
  return a.forecastName.localeCompare(b.forecastName);
};
export const ForecastTypeFilter = ({
  isDetail
}) => {
  const dispatch = useDispatch();
  const hasSuperAdminScope = useSelector(getHasSuperAdminScope);
  const forecastType = useSelector(getSelectedForecastType);
  const forecastTypes = useSelector(getForecastTypes);
  const isDealStageView = useSelector(getIsDealStageView);
  const selectedForecastIsDefault = useSelector(getSelectedForecastTypeIsDefault);
  const properties = useSelector(getProperties);
  const propertiesStatus = useSelector(getPropertiesStatus);
  const handleForecastTypeChange = useCallback(({
    target: {
      value
    }
  }) => {
    var _forecastTypes$find;
    void dispatch(setFilter({
      filter: FILTERS.FORECAST_TYPE,
      value
    }));
    const newForecastIsDefault = (_forecastTypes$find = forecastTypes.find(forecast => forecast.forecastTypeId === value)) === null || _forecastTypes$find === void 0 ? void 0 : _forecastTypes$find.defaultType;
    trackSwitchForecastType({
      screen: isDetail ? REP_DETAIL : REP_OVERVIEW,
      isDealStageView,
      isDefaultForecastBefore: selectedForecastIsDefault,
      isDefaultForecastAfter: newForecastIsDefault
    });
  }, [dispatch, forecastTypes, isDealStageView, isDetail, selectedForecastIsDefault]);
  const forecastTypeOptions = useMemo(() => forecastTypes.sort(forecastTypeSort).map(forecast => {
    return {
      title: forecast.forecastName,
      text: `${forecast.forecastName}${forecast.defaultType ? ` ${I18n.text('forecastType.default')}` : ''}`,
      value: forecast.forecastTypeId,
      help: getForecastTypeDescription({
        forecastType: forecast,
        properties
      }),
      disabled: forecast.amountOrDatePropertyRestrictedForUser
    };
  }), [forecastTypes, properties]);
  function OptionWrapper(_ref) {
    let {
        children,
        option
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return /*#__PURE__*/_jsx("span", Object.assign({}, rest, {
      children: /*#__PURE__*/_jsx(UITooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "forecastType.disabled"
        }),
        disabled: !option.disabled,
        children: children
      })
    }));
  }
  return /*#__PURE__*/_jsxs(View, {
    marginBlockEnd: "300",
    marginInlineEnd: "300",
    as: "div",
    children: [/*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      justifyContent: "start",
      display: "inline-flex",
      children: /*#__PURE__*/_jsx(ForecastSelectorTourStep, {
        children: /*#__PURE__*/_jsx(Flex, {
          justifyContent: "start",
          children: /*#__PURE__*/_jsx(UISelect, {
            className: "p-left-0 p-y-1",
            "data-coaching-tip": "forecast-type-selector",
            "data-test-id": "forecast-type-selector",
            menuWidth: "auto",
            buttonSize: "md",
            buttonUse: "transparent",
            itemComponent: OptionWrapper,
            valueRenderer: ({
              title
            }) => /*#__PURE__*/_jsx(Text, {
              color: "text-interactive-default",
              typography: "heading-400",
              children: title
            }),
            value: forecastType === null || forecastType === void 0 ? void 0 : forecastType.forecastTypeId,
            options: forecastTypeOptions,
            dropdownFooter: hasSuperAdminScope && /*#__PURE__*/_jsx(CreateForecastButton, {
              buttonLocation: "filter",
              buttonUse: "transparent",
              buttonSize: "medium",
              paddingInlineStart: "0"
            }),
            onChange: handleForecastTypeChange,
            placement: "bottom right"
          })
        })
      })
    }), /*#__PURE__*/_jsx(Flex, {
      flexDirection: "row",
      justifyContent: "space-between",
      children: propertiesStatus === UNINITIALIZED || propertiesStatus === STARTED ? /*#__PURE__*/_jsx(LoadingSpinner, {
        marginBlockStart: "200",
        marginBlockEnd: "200",
        marginInlineStart: "100",
        size: "extra-small"
      }) : /*#__PURE__*/_jsx(ForecastTypeDescription, {
        forecastType: forecastType,
        properties: properties
      })
    })]
  });
};